// Packages
import React, { useState, useRef, useEffect } from 'react';
import { Drawer } from '@react-kite-plus/drawer';
import { KiteButton } from '@kite/react-kite';

import { dedupeSelections, buildFilterState } from './filterUtils';

import { FilterDropdownWithChips } from 'components/FilterDropdownWithChips';

import { IOrderType } from 'pages/Home/Home';

// Styles
import './FilterDrawer.scss';

// Dropdown config interface
interface IDropdownConfig {
  searchKey: string;
  displayKey: string;
  label: string;
}

// Selection object interface
export interface ISelectionObject {
  [key: string]: string;
}

// Filter selection state interface
export interface IFilterSelection {
  [key: string]: string[];
}

// Filter options from API response
export interface IFilterOptions {
  [key: string]: string[];
}

// Props interface
export interface IFilterDrawerProps {
  open: boolean;
  orderTypes?: IOrderType[];
  filterOptions?: IFilterOptions;
  onApplyFilters?: (selectedObjects: IFilterSelection) => void;
  onclearFilter?: () => void;
  onClose: () => void;
  className?: string;
  onFiltersChange?: (selectedObjects: IFilterSelection) => void;
  activeFilters?: IFilterSelection;
}

const dropdownConfigs: IDropdownConfig[] = [
  {
    searchKey: 'orderType',
    displayKey: 'orderType',
    label: 'Order Type',
  },
  {
    searchKey: 'device',
    displayKey: 'device',
    label: 'Device Type',
  },
  {
    searchKey: 'lob',
    displayKey: 'lob',
    label: 'Line of Business',
  },
  {
    searchKey: 'status',
    displayKey: 'status',
    label: 'Status',
  },
];

const FilterDrawer: React.FC<IFilterDrawerProps> = ({
  open,
  orderTypes = [],
  filterOptions = {},
  onApplyFilters,
  onclearFilter,
  onClose,
  className = '',
  onFiltersChange,
  activeFilters = {},
}) => {
  const [selectedObjects, setSelectedObjects] = useState<IFilterSelection>({});
  const allSelections = useRef<ISelectionObject[]>([]);
  const prevFiltersRef = useRef<string>('');

  // Sync internal state with external activeFilters when they change
  useEffect(() => {
    const filtersJson = JSON.stringify(activeFilters);
    if (filtersJson === prevFiltersRef.current) return;
    prevFiltersRef.current = filtersJson;

    // Convert activeFilters back to allSelections format
    const newAllSelections: ISelectionObject[] = [];
    Object.entries(activeFilters).forEach(([key, values]) => {
      if (Array.isArray(values)) {
        values.forEach((value) => {
          newAllSelections.push({ [key]: value });
        });
      }
    });
    allSelections.current = newAllSelections;
    setSelectedObjects(activeFilters);
  }, [activeFilters]);

  const handleSelectionChange = (
    key: string,
    selections: ISelectionObject | ISelectionObject[]
  ) => {
    const selectionsArray = Array.isArray(selections)
      ? selections
      : [selections];

    // For each selection, check if it should be added or removed
    selectionsArray.forEach((selection) => {
      const existingIndex = allSelections.current.findIndex(
        (item) => Object.keys(item)[0] === key && item[key] === selection[key]
      );

      if (existingIndex !== -1) {
        allSelections.current = allSelections.current.filter(
          (_, index) => index !== existingIndex
        );
      } else {
        allSelections.current.push(selection);
      }
    });

    // Dedupe selections to ensure no duplicates
    allSelections.current = dedupeSelections(allSelections.current);

    const updatedFilters = buildFilterState(
      allSelections.current,
      dropdownConfigs
    );
    setSelectedObjects(updatedFilters);
    if (onFiltersChange) onFiltersChange(updatedFilters);
  };

  const handleRemoveSelection = (key: string, value: ISelectionObject) => {
    // Remove the selected item for the given key
    allSelections.current = allSelections.current.filter(
      (item) => !(item[key] === value[key] && Object.keys(item)[0] === key)
    );

    // Build the new filter state using the utility
    const updatedFilters = buildFilterState(
      allSelections.current,
      dropdownConfigs
    );

    setSelectedObjects(updatedFilters);
    onFiltersChange?.(updatedFilters);
  };

  const clearAllFilters = () => {
    allSelections.current = [];
    const cleared: IFilterSelection = {};
    dropdownConfigs.forEach(({ searchKey }) => (cleared[searchKey] = []));
    setSelectedObjects(cleared);
    if (onclearFilter) onclearFilter();
    if (onFiltersChange) onFiltersChange(cleared);
  };

  // =============================================
  // Return
  // =============================================
  return (
    <Drawer
      className={`filter-drawer ${className}`}
      isOpen={open}
      onClose={onClose}
      width="450px"
    >
      <h3 className="filter-drawer__title kite-type-style--title-5">Filters</h3>

      {/* Dynamically render dropdowns */}
      {dropdownConfigs.map(({ searchKey, displayKey, label }) => {
        const selectedItems = allSelections.current.filter(
          (item) => Object.keys(item)[0] === searchKey
        );

        // Use API-provided filter options if available, otherwise fall back to orderTypes data
        const options = filterOptions[searchKey]?.length
          ? filterOptions[searchKey]
          : undefined;

        return (
          <FilterDropdownWithChips
            key={searchKey}
            data={orderTypes}
            searchKey={searchKey}
            displayKey={displayKey}
            label={label}
            selectedItems={selectedItems}
            options={options}
            onSelectionChange={(selections) =>
              handleSelectionChange(searchKey, selections as ISelectionObject[])
            }
            onRemoveSelection={(value) =>
              handleRemoveSelection(searchKey, value as ISelectionObject)
            }
          />
        );
      })}

      <div className="clear-all-btn-container">
        <KiteButton
          variant="borderless"
          onClick={clearAllFilters}
          size="shrinkwrap"
          className="clear-all-btn"
        >
          Clear All Filters
        </KiteButton>
      </div>

      {/* Cancel and Apply buttons */}
      <div className="filter-drawer-btn-container">
        <KiteButton
          className="filter-drawer-btn cancel-btn"
          variant="secondary"
          onClick={onClose}
        >
          Cancel
        </KiteButton>
        <KiteButton
          className="filter-drawer-btn apply-filters-btn"
          onClick={() => {
            if (onApplyFilters) {
              onApplyFilters(selectedObjects); // Pass selectedObjects to the onApplyFilters callback
            }
          }}
        >
          Apply
        </KiteButton>
      </div>
    </Drawer>
  );
};

FilterDrawer.displayName = 'FilterDrawer';

export default FilterDrawer;
